// @ts-nocheck
/*
 * Port of views/dashboard/voicemails/list.js (embedded panel).
 * APIs: API_VOICEMAILS_LIST, API_VOICEMAILS_AUDIO, API_AGENTS_LIST,
 *       API_AGENT_GROUPS_LIST, API_CALL_CENTRE_LIST, API_COMPANY_LIST.
 * Role-scoped filter set, server-side pagination, inline audio.
 * Columns: Received, Agent, Group, DID, Size, Listen.
 * Caller was dropped - the voicemails table has no caller column, so it could
 * only ever render a dash.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'sonner';
import { usePersistedState } from '@/lib/filters/persisted-state';
import { Filter, Headphones, Play, RefreshCw, Square, Trash2 } from 'lucide-react';
import type { ColumnDef } from '@tanstack/react-table';
import { getApiCall } from '@/service/httpCall';
import { getApiCallShared } from '@/service/sharedGet';
import { useSectionRefresh } from '@/features/cti2/components/DashboardSection';
import {
  API_VOICEMAILS_LIST,
  API_VOICEMAILS_AUDIO,
  API_AGENTS_LIST,
  API_AGENT_GROUPS_LIST,
  API_CALL_CENTRE_LIST,
  API_COMPANY_LIST,
} from '@/service/url';
import { STORAGE_API_BASE_URL, USER_ROLES } from '@/utilities/constant';
import { storageUrlOr } from '@/utilities/storageUrl';
import { isMediaReady, MEDIA_NOT_READY_TITLE } from '@/utilities/cdrMedia';
import { getUser } from '@/utilities/setting';
import { fetchMediaAudioUrl, revokeMediaAudioUrl } from '@/utilities/mediaAudio';
import { LIST_DEFAULT_PAGE_SIZE } from '@/utilities/listPagination';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { DataTable } from '@/components/data-table/DataTable';
import DeleteMediaDialog from './DeleteMediaDialog';
import { DateRangeFilter } from '@/components/filters/DateRangeFilter';

const TZ = 'Asia/Kolkata';
const MAX_RANGE_MONTHS = 3;

const formatDateOnlyLocal = (date) => {
  const off = date.getTimezoneOffset();
  return new Date(date.getTime() - off * 60000).toISOString().slice(0, 10);
};

const getDefaultDateRange = () => {
  const now = new Date();
  return {
    from: formatDateOnlyLocal(new Date(now.getFullYear(), now.getMonth(), 1)),
    to: formatDateOnlyLocal(now),
  };
};
const getMinAllowedFromDate = (months = MAX_RANGE_MONTHS) => {
  const d = new Date();
  d.setMonth(d.getMonth() - months);
  return formatDateOnlyLocal(d);
};
const isRangeWithinLimit = (from, to, months = MAX_RANGE_MONTHS) => {
  if (!from || !to) return true;
  const f = new Date(`${from}T00:00:00`);
  const maxTo = new Date(f);
  maxTo.setMonth(maxTo.getMonth() + months);
  return new Date(`${to}T00:00:00`).getTime() <= maxTo.getTime();
};

const DEFAULT_DATE_RANGE = getDefaultDateRange();
const TODAY = formatDateOnlyLocal(new Date());
const monthsLabel = (n) => `${n} month${n === 1 ? '' : 's'}`;

const formatBytes = (bytes) => {
  const n = Number(bytes) || 0;
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
};

const buildAudioUrl = (row) => {
  const path = API_VOICEMAILS_AUDIO.replace(':id', String(row?.id));
  const base = String(STORAGE_API_BASE_URL || '').replace(/\/+$/, '');
  const byId = `${base}${path.startsWith('/') ? path : `/${path}`}`;

  return storageUrlOr('voicemails', row?.blob_path, byId);
};

const fmtDateTime = (v) => {
  if (!v) return '-';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleString('en-IN', { timeZone: TZ, day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true });
};

export default function VoicemailsPanel({
  fromDate: fromDateProp,
  toDate: toDateProp,
  filterPortalTarget,
  onChanged,
  maxRangeMonths = MAX_RANGE_MONTHS,
}: any = {}) {
  const minFromDate = getMinAllowedFromDate(maxRangeMonths);
  const user = getUser();
  const role = user?.role;
  const isAgent = role === USER_ROLES.CALL_CENTRE_AGENT;
  const isCCAdmin = role === USER_ROLES.CALL_CENTRE_ADMIN;
  const showCompanyFilter = role === USER_ROLES.SUPERADMIN || role === USER_ROLES.SUB_SUPERADMIN || role === USER_ROLES.RESELLER;
  const showCallCenterFilter = !isAgent && !isCCAdmin;
  const showAgentGroupFilter = !isAgent;
  const showAgentFilter = !isAgent;

  const isDateControlled = fromDateProp !== undefined;
  // Mounted standalone (dashboard Call Media, CTI screens) or with parent-controlled
  // dates (cti2 AgentDashboardView): keep the two sets of filters apart.
  const persistKey = `dashboard/VoicemailsPanel:${isDateControlled ? 'controlled' : 'own'}`;

  const [fromDateState, setFromDateState] = usePersistedState(isDateControlled ? null : `${persistKey}:fromDate`, DEFAULT_DATE_RANGE.from);
  const [toDateState, setToDateState] = usePersistedState(isDateControlled ? null : `${persistKey}:toDate`, DEFAULT_DATE_RANGE.to);

  const fromDate = isDateControlled ? fromDateProp : (fromDateState && fromDateState < minFromDate ? minFromDate : fromDateState);
  const toDate = isDateControlled ? toDateProp : toDateState;
  const setFromDate = isDateControlled ? () => { } : setFromDateState;
  const setToDate = isDateControlled ? () => { } : setToDateState;
  const isFilterControlled = filterPortalTarget !== undefined;

  const [agentId, setAgentId] = usePersistedState(`${persistKey}:agentId`, '');
  const [agentGroupId, setAgentGroupId] = usePersistedState(`${persistKey}:agentGroupId`, '');
  const [callCenterId, setCallCenterId] = usePersistedState(`${persistKey}:callCenterId`, '');
  const [companyId, setCompanyId] = usePersistedState(`${persistKey}:companyId`, '');
  const [agents, setAgents] = useState([]);
  const [agentGroups, setAgentGroups] = useState([]);
  const [callCentres, setCallCentres] = useState([]);
  const [companies, setCompanies] = useState([]);

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = usePersistedState(`${persistKey}:page`, 1);
  const [pageSize, setPageSize] = usePersistedState(`${persistKey}:pageSize`, LIST_DEFAULT_PAGE_SIZE);
  const [loading, setLoading] = useState(false);
  const [playingId, setPlayingId] = useState(null);
  const [playingUrl, setPlayingUrl] = useState(null);
  const [deleteRow, setDeleteRow] = useState(null);
  const audioRef = useRef(null);

  // Only the call centre admin may delete; the API refuses everyone else too.
  const canDelete = getUser()?.role === USER_ROLES.CALL_CENTRE_ADMIN;

  const stopPlayback = useCallback(() => {
    if (audioRef.current) audioRef.current.pause();
    setPlayingId(null);
    setPlayingUrl(null);
  }, []);

  const startPlayback = useCallback(async (row) => {
    if (!isMediaReady(row)) return;
    let objectUrl;
    try {
      objectUrl = await fetchMediaAudioUrl(buildAudioUrl(row));
    } catch (err) {
      toast.error(err?.message || 'Unable to play voicemail audio');
      return;
    }
    setPlayingUrl(objectUrl);
    setPlayingId(row?.id);
  }, []);

  useEffect(() => {
    if (showAgentFilter) getApiCallShared(`${API_AGENTS_LIST}?fields=min`).then((r) => setAgents(Array.isArray(r) ? r : [])).catch(() => { });
    if (showAgentGroupFilter) getApiCall(API_AGENT_GROUPS_LIST, { fields: 'min' }).then((r) => setAgentGroups(Array.isArray(r) ? r : [])).catch(() => { });
    if (showCallCenterFilter) getApiCall(API_CALL_CENTRE_LIST).then((r) => setCallCentres(Array.isArray(r) ? r : [])).catch(() => { });
    if (showCompanyFilter) getApiCall(API_COMPANY_LIST).then((r) => setCompanies(Array.isArray(r) ? r : [])).catch(() => { });

  }, [showAgentFilter, showAgentGroupFilter, showCallCenterFilter, showCompanyFilter]);

  useEffect(() => {
    return () => { if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; } };
  }, []);

  useEffect(() => () => revokeMediaAudioUrl(playingUrl), [playingUrl]);

  const validateDates = useCallback(() => {
    if (!fromDate || !toDate) { toast.error('From date and To date are required'); return false; }
    if (fromDate < minFromDate) { toast.error(`From date cannot be earlier than the last ${monthsLabel(maxRangeMonths)}`); return false; }
    if (fromDate > toDate) { toast.error('From date cannot be after To date'); return false; }
    if (!isRangeWithinLimit(fromDate, toDate, maxRangeMonths)) { toast.error(`Date range cannot exceed ${monthsLabel(maxRangeMonths)}`); return false; }
    return true;
  }, [fromDate, toDate, minFromDate, maxRangeMonths]);

  const listSeqRef = useRef(0);
  const fetchVoicemails = useCallback(async (nextPage = page, nextPageSize = pageSize, silent = false) => {
    if (!validateDates()) return;
    const params = { from_date: fromDate, to_date: toDate, page: nextPage, limit: nextPageSize };
    if (showAgentFilter && agentId) params.agent_id = agentId;
    if (showAgentGroupFilter && agentGroupId) params.agent_group_id = agentGroupId;
    if (showCallCenterFilter && callCenterId) params.call_center_id = callCenterId;
    if (showCompanyFilter && companyId) params.company_id = companyId;
    const seq = ++listSeqRef.current;
    if (!silent) setLoading(true);
    try {
      const data = await getApiCall(API_VOICEMAILS_LIST, params);
      if (seq !== listSeqRef.current) return; // a newer page/filter request owns the table
      setRows(Array.isArray(data?.rows) ? data.rows : []);
      setTotal(Number(data?.total) || 0);
      setPage(Number(data?.page) || nextPage);
      setPageSize(Number(data?.limit) || nextPageSize);
    } catch {
      if (!silent && seq === listSeqRef.current) { setRows([]); setTotal(0); }
    } finally { if (seq === listSeqRef.current) setLoading(false); }
  }, [validateDates, fromDate, toDate, page, pageSize, agentId, agentGroupId, callCenterId, companyId, showAgentFilter, showAgentGroupFilter, showCallCenterFilter, showCompanyFilter]);

  // Mount loads the restored page; a later date change starts again at page 1.
  const datesMountedRef = useRef(false);
  useEffect(() => {
    if (isDateControlled) return; fetchVoicemails(page, pageSize);
  }, []);
  useEffect(() => {
    if (!isDateControlled) return;
    const firstRun = !datesMountedRef.current;
    datesMountedRef.current = true;
    fetchVoicemails(firstRun ? page : 1, pageSize);
  }, [fromDateProp, toDateProp]);

  const handleApplyFilters = () => fetchVoicemails(1, pageSize);
  const inSection = useSectionRefresh(() => fetchVoicemails(page, pageSize, true));
  const handleResetFilters = () => {
    setFromDate(DEFAULT_DATE_RANGE.from); setToDate(DEFAULT_DATE_RANGE.to);
    setAgentId(''); setAgentGroupId(''); setCallCenterId(''); setCompanyId('');
    setRows([]); setTotal(0); setPage(1);
  };

  const columns = useMemo<ColumnDef<any>[]>(
    () => [
      { id: 'created_at', header: 'Received', cell: ({ row }) => fmtDateTime(row.original.created_at) },
      { id: 'agent_name', header: 'Agent', cell: ({ row }) => row.original.agent_name || row.original.agent?.agent_name || '—' },
      { id: 'agent_group_name', header: 'Group', cell: ({ row }) => row.original.agent_group_name || row.original.agent_group?.group_name || '—' },
      { id: 'did_number', header: 'DID', cell: ({ row }) => row.original.did_number || row.original.did || '—' },
      { accessorKey: 'size', header: 'Size', cell: ({ row }) => formatBytes(row.original.size) },
      {
        id: 'listen',
        header: 'Listen',
        enableSorting: false,
        cell: ({ row }) => {
          const id = row.original.id;
          const isPlaying = playingId === id;
          const ready = isMediaReady(row.original);
          return (
            <div>
              <button
                type="button"
                disabled={!ready}
                onClick={(e) => {
                  e.stopPropagation();
                  if (isPlaying) stopPlayback();
                  else startPlayback(row.original);
                }}
                className={
                  'inline-flex h-7 w-7 items-center justify-center rounded-full border border-border disabled:cursor-not-allowed disabled:opacity-40 ' +
                  (isPlaying ? 'text-red-500' : 'text-primary')
                }
                title={!ready ? MEDIA_NOT_READY_TITLE : isPlaying ? 'Stop' : 'Listen'}
              >
                {isPlaying ? <Square className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5" />}
              </button>
              {isPlaying && playingUrl && (
                <audio
                  key={playingUrl}
                  ref={audioRef}
                  controls
                  autoPlay
                  src={playingUrl}
                  onEnded={stopPlayback}
                  className="mt-2 w-full"
                />
              )}
            </div>
          );
        },
      },
      ...(canDelete
        ? [
            {
              id: 'delete',
              header: '',
              enableSorting: false,
              cell: ({ row }) => (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setDeleteRow(row.original);
                  }}
                  className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-border text-destructive hover:bg-destructive/10"
                  title="Delete voicemail"
                  aria-label="Delete voicemail"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              ),
            },
          ]
        : []),
    ],
    [playingId, playingUrl, startPlayback, stopPlayback, canDelete],
  );

  return (
    <>
      <div className="mb-3 flex items-center justify-between">
        {!isDateControlled ? (
          <span className="flex items-center gap-2 text-sm font-bold text-foreground">
            <Headphones className="h-4 w-4 text-primary" />
            Voicemails
            {total > 0 && (
              <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-bold text-primary">{total}</span>
            )}
          </span>
        ) : total > 0 ? (
          <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-bold text-primary">{total} total</span>
        ) : (
          <span />
        )}
        {!inSection ? (
          <Button size="sm" variant="outline" onClick={handleApplyFilters} disabled={loading}>
            <RefreshCw className={'mr-1.5 h-3.5 w-3.5 ' + (loading ? 'animate-spin' : '')} />
            Refresh
          </Button>
        ) : null}
      </div>

      {!isFilterControlled ? (
        <div className="sticky top-10 z-[5] -mx-3 mb-3 border-b border-border/70 bg-card/95 px-3 py-3 backdrop-blur">
          <DateRangeFilter
            compact
            fromLabel="From date"
            toLabel="To date"
            value={{ from: fromDate, to: toDate }}
            min={minFromDate}
            max={TODAY}
            onChange={({ from, to }) => { setFromDate(from); setToDate(to); }}
            onApply={handleApplyFilters}
            applyLabel="Apply"
            onReset={handleResetFilters}
          >
          {showAgentFilter && (
            <div>
              <label className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Agent</label>
              <select value={agentId} onChange={(e) => setAgentId(e.target.value)} className="h-9 w-full min-w-[160px] rounded-md border border-border bg-background px-2 text-sm">
                <option value="">All agents</option>
                {agents.map((a) => (<option key={a.id} value={a.id}>{a.agent_name || a.username || `Agent #${a.id}`}</option>))}
              </select>
            </div>
          )}
          {showAgentGroupFilter && (
            <div>
              <label className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Agent Group</label>
              <select value={agentGroupId} onChange={(e) => setAgentGroupId(e.target.value)} className="h-9 w-full min-w-[160px] rounded-md border border-border bg-background px-2 text-sm">
                <option value="">All groups</option>
                {agentGroups.map((g) => (<option key={g.id} value={g.id}>{g.group_name || `Group #${g.id}`}</option>))}
              </select>
            </div>
          )}
          {showCallCenterFilter && (
            <div>
              <label className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Call Center</label>
              <select value={callCenterId} onChange={(e) => setCallCenterId(e.target.value)} className="h-9 w-full min-w-[160px] rounded-md border border-border bg-background px-2 text-sm">
                <option value="">All call centers</option>
                {callCentres.map((c) => (<option key={c.id} value={c.id}>{c.name || `#${c.id}`}</option>))}
              </select>
            </div>
          )}
          {showCompanyFilter && (
            <div>
              <label className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Company</label>
              <select value={companyId} onChange={(e) => setCompanyId(e.target.value)} className="h-9 w-full min-w-[160px] rounded-md border border-border bg-background px-2 text-sm">
                <option value="">All companies</option>
                {companies.map((c) => (<option key={c.id} value={c.id}>{c.name || `#${c.id}`}</option>))}
              </select>
            </div>
          )}
          </DateRangeFilter>
        </div>
      ) : (
        filterPortalTarget &&
        createPortal(
          <>
            {showAgentFilter && (
              <select value={agentId} onChange={(e) => setAgentId(e.target.value)} className="h-8 rounded-full border border-border bg-muted/60 px-3 text-[11px]">
                <option value="">All agents</option>
                {agents.map((a) => (<option key={a.id} value={a.id}>{a.agent_name || a.username || `Agent #${a.id}`}</option>))}
              </select>
            )}
            {showAgentGroupFilter && (
              <select value={agentGroupId} onChange={(e) => setAgentGroupId(e.target.value)} className="h-8 rounded-full border border-border bg-muted/60 px-3 text-[11px]">
                <option value="">All groups</option>
                {agentGroups.map((g) => (<option key={g.id} value={g.id}>{g.group_name || `Group #${g.id}`}</option>))}
              </select>
            )}
            {showCallCenterFilter && (
              <select value={callCenterId} onChange={(e) => setCallCenterId(e.target.value)} className="h-8 rounded-full border border-border bg-muted/60 px-3 text-[11px]">
                <option value="">All call centers</option>
                {callCentres.map((c) => (<option key={c.id} value={c.id}>{c.name || `#${c.id}`}</option>))}
              </select>
            )}
            {showCompanyFilter && (
              <select value={companyId} onChange={(e) => setCompanyId(e.target.value)} className="h-8 rounded-full border border-border bg-muted/60 px-3 text-[11px]">
                <option value="">All companies</option>
                {companies.map((c) => (<option key={c.id} value={c.id}>{c.name || `#${c.id}`}</option>))}
              </select>
            )}
            <Button size="sm" variant="outline" className="h-8 rounded-full" onClick={handleResetFilters}>Reset</Button>
            <Button size="sm" className="h-8 rounded-full" onClick={handleApplyFilters}>
              <Filter className="mr-1.5 h-3.5 w-3.5" />Apply
            </Button>
          </>,
          filterPortalTarget,
        )
      )}

      <DataTable
        tableId="dashboard.voicemails"
        columns={columns}
        data={rows}
        isLoading={loading}
        emptyTitle="No voicemails"
        emptyDescription="No voicemails found for the current filters."
        pageSize={pageSize}
        exportFilename="voicemails.csv"
      />

      {total > pageSize && (
        <div className="mt-3 flex items-center justify-between text-xs text-muted-foreground">
          <span>Server page {page} · {total} voicemails total</span>
          <div className="flex gap-2">
            <button
              disabled={page <= 1}
              onClick={() => fetchVoicemails(Math.max(1, page - 1), pageSize)}
              className="rounded border border-border px-2 py-1 disabled:opacity-40"
            >Prev</button>
            <button
              disabled={page * pageSize >= total}
              onClick={() => fetchVoicemails(page + 1, pageSize)}
              className="rounded border border-border px-2 py-1 disabled:opacity-40"
            >Next</button>
          </div>
        </div>
      )}

      <DeleteMediaDialog
        open={Boolean(deleteRow)}
        onOpenChange={(next) => !next && setDeleteRow(null)}
        type="voicemails"
        row={deleteRow}
        onDeleted={(id) => {
          setRows((prev) => prev.filter((r) => r.id !== id));
          setTotal((t) => Math.max(0, t - 1));
          onChanged?.();
        }}
      />
    </>
  );
}
